import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  Search,
  Plus,
  Loader,
  Trash2,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronUp,
  ChevronDown,
  ExternalLink,
  List,
  LayoutGrid,
  AlertCircle,
} from 'lucide-react'
import KeywordDetailModal, { type KeywordDetailData } from './KeywordDetailModal'

interface RankingKeywordRow {
  id: number
  keyword: string
  device: string
  location: string
  position: number | null
  url: string | null
  delta: number | null
  volume: number | null
  manualPosition: number | null
  checkedAt: string | null
}

interface SearchSettings {
  country: string
  language: string
}

interface KeywordTableProps {
  keywords: RankingKeywordRow[]
  onAdd: (keyword: string) => Promise<void>
  onDelete: (id: number) => Promise<void>
  onManualPosition?: (id: number, position: number | null) => Promise<void>
  adding: boolean
  addError: string | null
  projectId: string
  maxKeywords: number
  searchSettings?: SearchSettings
}

type FilterTab = 'all' | 'top3' | 'top10' | 'top30' | 'unranked'
type SortKey = 'keyword' | 'position' | 'delta' | 'volume' | 'checkedAt'
type SortDir = 'asc' | 'desc'

const FILTER_TABS: { key: FilterTab; label: string }[] = [
  { key: 'all', label: 'Tous' },
  { key: 'top3', label: 'Top 3' },
  { key: 'top10', label: 'Top 10' },
  { key: 'top30', label: 'Top 30' },
  { key: 'unranked', label: 'Non classe' },
]

function matchesFilter(kw: RankingKeywordRow, filter: FilterTab): boolean {
  const p = kw.position
  switch (filter) {
    case 'all': return true
    case 'top3': return p !== null && p >= 1 && p <= 3
    case 'top10': return p !== null && p >= 1 && p <= 10
    case 'top30': return p !== null && p >= 1 && p <= 30
    case 'unranked': return p === null
  }
}

function formatRelativeDate(iso: string | null): string {
  if (!iso) return '--'
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diffMs = now - then
  if (diffMs < 0) return '--'
  const minutes = Math.floor(diffMs / 60_000)
  if (minutes < 1) return "a l'instant"
  if (minutes < 60) return `il y a ${minutes}min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `il y a ${hours}h`
  const days = Math.floor(hours / 24)
  if (days <= 7) return `il y a ${days}j`
  return new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
}

function formatVolume(v: number | null): string {
  if (v === null) return '--'
  return v.toLocaleString('fr-FR')
}

function positionBadge(pos: number | null, hasManual: boolean, onClick: () => void) {
  const baseClass = 'inline-flex items-center justify-center rounded-lg px-2.5 py-1 text-xs font-semibold tabular-nums cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-orion-primary/40 transition-all'

  if (pos === null) {
    return (
      <button onClick={onClick} className={`${baseClass} bg-gray-100 text-gray-400 font-medium`}>
        --
      </button>
    )
  }
  let cls = 'bg-gray-100 text-gray-500'
  if (pos === 1) cls = 'bg-emerald-500/15 text-emerald-500 font-bold'
  else if (pos <= 3) cls = 'bg-green-500/15 text-green-600'
  else if (pos <= 10) cls = 'bg-blue-500/15 text-blue-600'
  else if (pos <= 30) cls = 'bg-yellow-500/15 text-yellow-600'

  return (
    <button onClick={onClick} className={`${baseClass} ${cls} relative`}>
      {pos}
      {hasManual && (
        <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 border border-white" title="Position manuelle définie" />
      )}
    </button>
  )
}

function deltaCell(delta: number | null) {
  if (delta === null) {
    return <span className="text-xs text-[#D1D5DB]">--</span>
  }
  if (delta === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs text-[#8B8FA7]">
        <Minus size={12} strokeWidth={1.5} />
        0
      </span>
    )
  }
  if (delta > 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-emerald-600">
        <TrendingUp size={13} strokeWidth={1.5} />
        +{delta}
      </span>
    )
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-xs font-semibold text-red-500">
      <TrendingDown size={13} strokeWidth={1.5} />
      {delta}
    </span>
  )
}

function truncateUrl(url: string, max = 45): string {
  const clean = url.replace(/^https?:\/\//, '').replace(/\/$/, '')
  if (clean.length <= max) return clean
  return clean.slice(0, max) + '...'
}

export default function KeywordTable({
  keywords,
  onAdd,
  onDelete,
  onManualPosition,
  adding,
  addError,
  projectId,
  maxKeywords,
  searchSettings,
}: KeywordTableProps) {
  const [input, setInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<FilterTab>('all')
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [deletingId, setDeletingId] = useState<number | null>(null)
  const [selectedKeyword, setSelectedKeyword] = useState<KeywordDetailData | null>(null)

  const handleAdd = async () => {
    const trimmed = input.trim()
    if (!trimmed) return
    await onAdd(trimmed)
    setInput('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  const handleDelete = async (id: number) => {
    setDeletingId(id)
    try {
      await onDelete(id)
    } finally {
      setDeletingId(null)
    }
  }

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('asc')
    }
  }

  const rows = useMemo(() => {
    let filtered = keywords.filter(kw => matchesFilter(kw, filter))
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      filtered = filtered.filter(kw => kw.keyword.toLowerCase().includes(q))
    }
    if (sortKey) {
      filtered = [...filtered].sort((a, b) => {
        if (sortKey === 'keyword') {
          const cmp = a.keyword.localeCompare(b.keyword, 'fr')
          return sortDir === 'asc' ? cmp : -cmp
        }
        if (sortKey === 'checkedAt') {
          const at = a.checkedAt ? new Date(a.checkedAt).getTime() : 0
          const bt = b.checkedAt ? new Date(b.checkedAt).getTime() : 0
          return sortDir === 'asc' ? at - bt : bt - at
        }
        const av = a[sortKey as 'position' | 'delta' | 'volume']
        const bv = b[sortKey as 'position' | 'delta' | 'volume']
        if (av === null && bv === null) return 0
        if (av === null) return 1
        if (bv === null) return -1
        return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
      })
    }
    return filtered
  }, [keywords, filter, searchQuery, sortKey, sortDir])

  const sortIcon = (key: SortKey) => {
    if (sortKey !== key) return null
    return sortDir === 'asc'
      ? <ChevronUp size={13} strokeWidth={1.5} />
      : <ChevronDown size={13} strokeWidth={1.5} />
  }

  const filterCount = (tab: FilterTab) => keywords.filter(kw => matchesFilter(kw, tab)).length

  return (
    <>
    <div className="rounded-xl border border-[#E2E4ED] bg-white overflow-hidden">
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-semibold text-[#1B2559]">Presentation des mots-cles</h3>
            <span className="text-xs text-[#8B8FA7] tabular-nums">{keywords.length}/{maxKeywords}</span>
          </div>
          <div className="flex items-center gap-2">
            {keywords.length > 5 && (
              <div className="relative">
                <Search
                  size={14}
                  strokeWidth={1.25}
                  className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#8B8FA7]"
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Filtrer..."
                  className="w-36 rounded-xl border border-[#E2E4ED] bg-[#F8F9FC] py-1.5 pl-8 pr-3 text-xs text-[#1B2559] placeholder:text-[#8B8FA7] outline-none focus:border-orion-primary focus:ring-1 focus:ring-orion-primary/30 transition-colors"
                />
              </div>
            )}
            <div className="flex items-center gap-1">
              <button className="p-1.5 rounded-lg bg-orion-primary text-white">
                <List size={14} strokeWidth={1.5} />
              </button>
              <button className="p-1.5 rounded-lg text-[#8B8FA7] hover:bg-[#F4F5FA] transition-colors">
                <LayoutGrid size={14} strokeWidth={1.5} />
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1 mb-4 flex-wrap">
          {FILTER_TABS.map(tab => {
            const count = filterCount(tab.key)
            const active = filter === tab.key
            return (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                  active
                    ? 'bg-orion-primary text-white'
                    : 'bg-[#F4F5FA] text-[#555A73] hover:bg-[#E2E4ED]'
                }`}
              >
                {tab.label}
                <span className={`tabular-nums ${active ? 'text-white/70' : 'text-[#8B8FA7]'}`}>
                  {count}
                </span>
              </button>
            )
          })}
        </div>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search
              size={15}
              strokeWidth={1.25}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8B8FA7]"
            />
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ajouter un mot-cle..."
              className="w-full rounded-xl border border-[#E2E4ED] bg-[#F8F9FC] py-2 pl-9 pr-4 text-sm text-[#1B2559] placeholder:text-[#8B8FA7] outline-none focus:border-orion-primary focus:ring-1 focus:ring-orion-primary/30 transition-colors"
            />
          </div>
          <button
            onClick={handleAdd}
            disabled={adding || !input.trim()}
            className="inline-flex items-center gap-1.5 rounded-xl bg-orion-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-orion-primary-hover disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            {adding ? (
              <Loader size={14} strokeWidth={1.25} className="animate-spin" />
            ) : (
              <Plus size={14} strokeWidth={1.5} />
            )}
            Ajouter
          </button>
        </div>

        {addError && (
          <div className="flex items-center gap-1.5 mt-2 text-xs text-red-500">
            <AlertCircle size={13} strokeWidth={1.5} />
            {addError}
          </div>
        )}
      </div>

      {rows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-14 text-center">
          <div className="mb-2.5 flex h-11 w-11 items-center justify-center rounded-xl bg-[#F4F5FA]">
            <Search size={18} strokeWidth={1.25} className="text-[#8B8FA7]" />
          </div>
          <p className="text-sm font-medium text-[#1B2559]">Aucun mot-cle</p>
          <p className="mt-0.5 text-xs text-[#8B8FA7]">
            {keywords.length === 0
              ? 'Ajoutez un mot-cle ci-dessus pour commencer'
              : 'Aucun resultat pour ce filtre'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-t border-[#E2E4ED] bg-[#F8F9FC]">
                <th
                  className="px-5 py-2.5 text-left text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider cursor-pointer select-none"
                  onClick={() => toggleSort('keyword')}
                >
                  <span className="inline-flex items-center gap-1">Mot-cle {sortIcon('keyword')}</span>
                </th>
                <th
                  className="px-3 py-2.5 text-center text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider cursor-pointer select-none w-20"
                  onClick={() => toggleSort('position')}
                >
                  <span className="inline-flex items-center gap-1 justify-center">Pos. {sortIcon('position')}</span>
                </th>
                <th
                  className="px-3 py-2.5 text-center text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider cursor-pointer select-none w-20"
                  onClick={() => toggleSort('delta')}
                >
                  <span className="inline-flex items-center gap-1 justify-center">Evol. {sortIcon('delta')}</span>
                </th>
                <th
                  className="px-3 py-2.5 text-center text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider cursor-pointer select-none w-20"
                  onClick={() => toggleSort('volume')}
                >
                  <span className="inline-flex items-center gap-1 justify-center">Vol. {sortIcon('volume')}</span>
                </th>
                <th className="px-3 py-2.5 text-left text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider">
                  URL classee
                </th>
                <th
                  className="px-3 py-2.5 text-center text-[10px] font-semibold text-[#8B8FA7] uppercase tracking-wider cursor-pointer select-none w-24"
                  onClick={() => toggleSort('checkedAt')}
                >
                  <span className="inline-flex items-center gap-1 justify-center">Verif. {sortIcon('checkedAt')}</span>
                </th>
                <th className="px-3 py-2.5 w-12" />
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E2E4ED]/60">
              {rows.map(kw => (
                <tr key={kw.id} className="group hover:bg-[#F4F5FA]/50 transition-colors">
                  <td className="px-5 py-2.5">
                    <Link
                      to={`/p/${projectId}/rankings/${kw.id}`}
                      className="font-medium text-[#1B2559] hover:text-orion-primary transition-colors"
                    >
                      {kw.keyword}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    {positionBadge(
                      kw.position,
                      kw.manualPosition !== null,
                      () => setSelectedKeyword({
                        id: kw.id,
                        keyword: kw.keyword,
                        position: kw.position,
                        manualPosition: kw.manualPosition,
                        url: kw.url,
                        checkedAt: kw.checkedAt,
                        device: kw.device,
                        location: kw.location,
                      })
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center">{deltaCell(kw.delta)}</td>
                  <td className="px-3 py-2.5 text-center text-[#555A73] tabular-nums">
                    {formatVolume(kw.volume)}
                  </td>
                  <td className="px-3 py-2.5">
                    {kw.url ? (
                      <a
                        href={kw.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 max-w-[220px] text-[#555A73] hover:text-orion-primary transition-colors"
                        title={kw.url}
                      >
                        <span className="truncate text-xs">{truncateUrl(kw.url)}</span>
                        <ExternalLink size={11} strokeWidth={1.25} className="shrink-0 opacity-50" />
                      </a>
                    ) : (
                      <span className="text-xs text-[#D1D5DB]">--</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center text-xs text-[#8B8FA7]">
                    {formatRelativeDate(kw.checkedAt)}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <button
                      onClick={() => handleDelete(kw.id)}
                      disabled={deletingId === kw.id}
                      className="inline-flex items-center justify-center rounded-lg p-1.5 text-[#D1D5DB] transition-all hover:bg-red-50 hover:text-red-500 opacity-0 group-hover:opacity-100 disabled:opacity-50"
                      title="Supprimer"
                    >
                      {deletingId === kw.id ? (
                        <Loader size={14} strokeWidth={1.25} className="animate-spin" />
                      ) : (
                        <Trash2 size={14} strokeWidth={1.25} />
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>

    {selectedKeyword && (
      <KeywordDetailModal
        keyword={selectedKeyword}
        searchSettings={searchSettings}
        projectId={projectId}
        onClose={() => setSelectedKeyword(null)}
        onSaveManualPosition={async (id, position) => {
          await onManualPosition?.(id, position)
          setSelectedKeyword(prev => prev ? { ...prev, manualPosition: position } : null)
        }}
      />
    )}
    </>
  )
}
